import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiCall, downloadFile, API_URL } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  ShieldAlert, Users, Briefcase, Search, RefreshCw, Loader2, ArrowRight,
  Clock, History, Building2, ExternalLink, X, FileDown, FileText,
} from 'lucide-react';
import { startImpersonation } from '../../utils/impersonation';

const SUPPORT_SCOPES = [
  { key: 'all',       label: 'Full access', hint: 'Every route on their dashboard' },
  { key: 'payments',  label: 'Payments',    hint: 'Wallet, Stripe, invoices' },
  { key: 'workers',   label: 'Workers',     hint: 'Roster, agent-workers' },
  { key: 'jobs',      label: 'Jobs',        hint: 'Shifts, matching' },
  { key: 'locations', label: 'Locations',   hint: 'Programs, group homes' },
  { key: 'settings',  label: 'Settings',    hint: 'Org profile, user management' },
];

const ROLE_TINT = {
  employer: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: Briefcase, label: 'Employer' },
  agent:    { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: Users,   label: 'Agency' },
};

function fmtTimeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function AdminSupportModePanel() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [starting, setStarting] = useState(null);
  const [modalTarget, setModalTarget] = useState(null); // { org, mode }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        apiCall('/api/admin/orgs'),
        apiCall('/api/admin/impersonate/audit?limit=20'),
      ]);
      setOrgs(a.orgs || []);
      setAudit(b.entries || []);
    } catch (e) {
      toast.error('Failed to load data', { description: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitEnter = async ({ org, mode, reason, scopes }) => {
    setStarting(`${org._id}:${mode}`);
    try {
      const r = await startImpersonation(org, { mode, reason, scopes });
      if (r.status === 'pending') {
        toast.success('Request sent — waiting for the org owner to approve.', {
          description: "You'll be able to enter Support Mode once they accept.",
        });
        setModalTarget(null);
        load();
      } else {
        toast.success(`Support Mode active — ${org.full_name || org.email}`);
        setModalTarget(null);
        const dest = org.role === 'agent' ? '/agent' : '/employer';
        navigate(dest);
      }
    } catch (e) {
      toast.error('Could not start Support Mode', { description: e.message });
    }
    setStarting(null);
  };

  const filtered = orgs.filter((o) => {
    if (roleFilter !== 'all' && o.role !== roleFilter) return false;
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return `${o.full_name} ${o.email} ${o.company_name || ''}`.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="admin-support-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 via-red-600 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-3 bg-white/15 backdrop-blur-sm rounded-xl ring-1 ring-white/20">
              <ShieldAlert className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Support Mode</h2>
              <p className="text-sm text-white/85 mt-0.5">Act on behalf of any employer or agency that requests assistance. Every action is fully audited.</p>
            </div>
          </div>
          <button
            onClick={load}
            className="p-2.5 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur-md ring-1 ring-white/20"
            title="Refresh"
            data-testid="admin-sm-refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* Org picker */}
        <div className="xl:col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company / contact / email"
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                data-testid="admin-sm-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All', count: orgs.length },
                { id: 'employer', label: 'Employers', count: orgs.filter((o) => o.role === 'employer').length },
                { id: 'agent', label: 'Agencies', count: orgs.filter((o) => o.role === 'agent').length },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setRoleFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition inline-flex items-center gap-1.5 ${
                    roleFilter === f.id ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`admin-sm-filter-${f.id}`}
                >
                  {f.label} <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${roleFilter === f.id ? 'bg-white/20' : 'bg-white text-slate-500'}`}>{f.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="divide-y divide-slate-100" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-12">No orgs match the filter</p>
            ) : (
              filtered.map((o) => {
                const meta = ROLE_TINT[o.role] || ROLE_TINT.employer;
                const Icon = meta.icon;
                return (
                  <div key={o._id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 transition" data-testid={`admin-sm-org-row-${o._id}`}>
                    <div className={`p-2 rounded-lg ${meta.bg} flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${meta.text}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900 truncate">{o.company_name || o.full_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{o.email} <span className="mx-1.5 text-slate-300">·</span><span className="capitalize">{meta.label}</span></p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => setModalTarget({ org: o, mode: 'consent' })}
                        disabled={!!starting}
                        className="px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold inline-flex items-center gap-1 shadow-sm disabled:opacity-50"
                        data-testid={`admin-sm-request-${o._id}`}
                      >
                        <ShieldAlert className="w-3.5 h-3.5" />
                        Request access
                      </button>
                      <button
                        onClick={() => setModalTarget({ org: o, mode: 'forced' })}
                        disabled={!!starting}
                        className="px-2.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold inline-flex items-center gap-1 shadow-sm disabled:opacity-50"
                        title="Emergency force entry — bypasses employer consent, fully audited"
                        data-testid={`admin-sm-force-${o._id}`}
                      >
                        <ArrowRight className="w-3.5 h-3.5" />
                        Force entry
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Audit feed */}
        <div className="xl:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col" style={{ maxHeight: '650px' }}>
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><History className="w-4 h-4 text-rose-500" /> Recent Activity</h3>
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Audit log</span>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="admin-sm-audit-list">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-rose-500" /></div>
            ) : audit.length === 0 ? (
              <div className="text-center py-12 px-3">
                <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-xs text-slate-500">No support sessions yet</p>
                <p className="text-[10px] text-slate-400 mt-1">All admin actions on behalf of orgs will appear here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {(() => {
                  const seenSessions = new Set();
                  return audit.map((entry) => {
                    const isStart = entry.path === 'impersonation:start';
                    const isStop = entry.path === 'impersonation:stop';
                    // Show the "Download report" affordance once per session,
                    // pinned to the first row we see. Newest-first ordering
                    // means the row will be near the top of that session.
                    const showDownload = entry.session_id && !seenSessions.has(entry.session_id);
                    if (showDownload) seenSessions.add(entry.session_id);
                    return (
                      <li key={entry._id} className="px-3 py-2.5 text-xs" data-testid={`admin-sm-audit-row-${entry._id}`}>
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            isStart ? 'bg-rose-100 text-rose-700' : isStop ? 'bg-slate-100 text-slate-600' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {isStart ? 'START' : isStop ? 'STOP' : entry.method}
                          </span>
                          <span className="text-[10px] text-slate-400">{fmtTimeAgo(entry.timestamp)}</span>
                        </div>
                        <p className="text-slate-800 font-semibold leading-tight inline-flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-slate-500" /> {entry.org_name || entry.org_email || 'Unknown'}
                        </p>
                        <p className="text-[11px] text-slate-500 truncate">by {entry.admin_name || entry.admin_email}</p>
                        {!isStart && !isStop && entry.path && (
                          <p className="text-[10px] text-slate-400 mt-0.5 truncate font-mono">{entry.path}</p>
                        )}
                        {showDownload && (
                          <div className="mt-1.5 flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Session report</span>
                            <a
                              href={`${API_URL}/api/admin/support-sessions/${entry.session_id}/diff.pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded bg-red-100 text-red-700 hover:bg-red-200 px-1.5 py-0.5 text-[10px] font-semibold"
                              data-testid={`admin-sm-diff-pdf-${entry.session_id}`}
                              title="Download PDF report of every action taken during this session"
                            >
                              <FileDown className="w-2.5 h-2.5" /> PDF
                            </a>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const r = await apiCall(`/api/admin/support-sessions/${entry.session_id}/diff`);
                                  const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `support-session-${entry.session_id}.json`;
                                  document.body.appendChild(a); a.click(); a.remove();
                                  URL.revokeObjectURL(url);
                                } catch (err) {
                                  toast.error('Report failed', { description: err.message });
                                }
                              }}
                              className="inline-flex items-center gap-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold"
                              data-testid={`admin-sm-diff-json-${entry.session_id}`}
                              title="Download the raw JSON of every action captured during this session"
                            >
                              <FileText className="w-2.5 h-2.5" /> JSON
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  });
                })()}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Helper */}
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-xs text-rose-900 flex gap-2">
        <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>
          <span className="font-bold">How Support Mode works:</span> Choose <b>Request access</b> to send a consent request the org owner must approve, or <b>Force entry</b> for emergencies (mandatory written reason). Each session lasts 4 hours max, is scoped to the areas you pick, and every action is written to the audit log below. The org owner can revoke your session at any time.
        </p>
      </div>

      {modalTarget && (
        <EnterSupportModal
          org={modalTarget.org}
          mode={modalTarget.mode}
          submitting={starting === `${modalTarget.org._id}:${modalTarget.mode}`}
          onCancel={() => setModalTarget(null)}
          onSubmit={(payload) => submitEnter({ ...modalTarget, ...payload })}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// EnterSupportModal — reason + scope picker (iter164). Mounted through
// ReactDOM.createPortal on document.body so it always sits above the
// admin dashboard's own overlays. iter165: scope templates saved to
// localStorage as `support_scope_templates` — one-click presets.
// ---------------------------------------------------------------------
const TEMPLATE_STORE_KEY = 'support_scope_templates_v1';

function loadTemplates() {
  try {
    const raw = localStorage.getItem(TEMPLATE_STORE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function saveTemplates(items) {
  try { localStorage.setItem(TEMPLATE_STORE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
}

function EnterSupportModal({ org, mode, submitting, onCancel, onSubmit }) {
  const [reason, setReason] = useState('');
  const [scopes, setScopes] = useState(['all']);
  const [templates, setTemplates] = useState(loadTemplates);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  const toggleScope = (key) => {
    if (key === 'all') {
      setScopes(['all']);
      return;
    }
    setScopes((prev) => {
      const withoutAll = prev.filter((s) => s !== 'all');
      if (withoutAll.includes(key)) {
        const next = withoutAll.filter((s) => s !== key);
        return next.length > 0 ? next : ['all'];
      }
      return [...withoutAll, key];
    });
  };

  const applyTemplate = (tpl) => {
    setScopes(tpl.scopes || ['all']);
    if (tpl.reason && !reason) setReason(tpl.reason);
  };

  const saveCurrentAsTemplate = () => {
    const name = newTemplateName.trim() || `Preset ${templates.length + 1}`;
    const next = [...templates, { id: Date.now(), name, scopes, reason: reason.slice(0, 200) }];
    setTemplates(next);
    saveTemplates(next);
    setNewTemplateName('');
    setSavingTemplate(false);
    toast.success(`Saved "${name}" for one-click reuse.`);
  };

  const deleteTemplate = (id) => {
    const next = templates.filter((t) => t.id !== id);
    setTemplates(next);
    saveTemplates(next);
  };

  const canSubmit = mode === 'forced' ? reason.trim().length >= 5 : true;
  const title = mode === 'forced' ? 'Emergency: Force entry' : 'Request support access';

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="admin-sm-modal"
    >
      <div
        className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-5 py-4 text-white ${
          mode === 'forced'
            ? 'bg-gradient-to-r from-rose-600 to-orange-600'
            : 'bg-gradient-to-r from-indigo-600 to-fuchsia-600'
        }`}>
          <div>
            <p className="text-[10px] uppercase tracking-wider bg-white/20 rounded px-1.5 py-0.5 inline-block">{mode}</p>
            <h3 className="mt-1 text-lg font-bold flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> {title}
            </h3>
            <p className="text-xs text-white/90 truncate">
              {org.full_name || org.company_name || org.email} · {org.email}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg hover:bg-white/15"
            data-testid="admin-sm-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Reason {mode === 'forced' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={mode === 'forced'
                ? 'Explain the emergency (≥ 5 chars) — this reason is audited.'
                : 'Describe what you need to look at. The org owner sees this text.'}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              data-testid="admin-sm-modal-reason"
            />
            {mode === 'forced' && reason.trim().length < 5 && (
              <p className="text-xs text-red-500 mt-1">A written reason (≥ 5 chars) is required.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-700">Scope</label>
              {!savingTemplate ? (
                <button
                  type="button"
                  onClick={() => setSavingTemplate(true)}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  data-testid="admin-sm-modal-save-template-btn"
                >
                  Save as preset
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="Preset name"
                    className="rounded-md border border-slate-300 px-2 py-1 text-xs w-32"
                    data-testid="admin-sm-modal-template-name"
                  />
                  <button
                    type="button"
                    onClick={saveCurrentAsTemplate}
                    className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white hover:bg-indigo-700"
                    data-testid="admin-sm-modal-template-save"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSavingTemplate(false); setNewTemplateName(''); }}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-2">
              Limit the session to specific areas. Pick "Full access" to keep it broad.
            </p>
            {templates.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid="admin-sm-modal-templates">
                {templates.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-700"
                  >
                    <button
                      type="button"
                      onClick={() => applyTemplate(t)}
                      className="font-semibold hover:text-indigo-700"
                      data-testid={`admin-sm-modal-template-${t.id}`}
                      title={`Apply preset "${t.name}"`}
                    >
                      {t.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteTemplate(t.id)}
                      className="ml-0.5 text-slate-400 hover:text-red-500"
                      title="Delete preset"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {SUPPORT_SCOPES.map((s) => {
                const active = scopes.includes(s.key);
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggleScope(s.key)}
                    className={`text-left rounded-lg border px-3 py-2 text-xs transition ${
                      active
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`admin-sm-modal-scope-${s.key}`}
                  >
                    <span className="font-semibold block">{s.label}</span>
                    <span className="text-[10px] text-slate-500">{s.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit({ reason: reason.trim(), scopes })}
            disabled={!canSubmit || submitting}
            className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              mode === 'forced' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
            data-testid="admin-sm-modal-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
            {mode === 'forced' ? 'Force entry now' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(modal, document.body) : modal;
}
